/**
 * 🗃 메일 쓰기 창 [+ NAS 파일] — NAS 폴더를 눌러 다니며 파일을 체크해 첨부 (v2.313.0 · 개발요청 #27)
 *
 * 접속은 탐색기와 같은 **본인 NAS 계정**이다 — 보이는 것도, 보낼 때 서버가 가져오는 것도 그 계정이 볼 수 있는 파일뿐.
 *   화면은 파일을 올리지 않는다: 고른 좌표(폴더 + 이름)만 첨부 목록에 올리고, 내용은 발송 때 서버가 가져온다.
 *
 * ⭐폴더를 옮겨도 고른 것은 남는다(여러 폴더에서 모아 붙일 수 있게) — 열쇠는 폴더 + 이름(nasFileKey).
 * ⭐메일 한도(25MB)를 혼자 넘는 파일은 처음부터 못 고르게 한다 — 보내기를 눌러서야 거절되면 다시 찾아야 한다.
 */

import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { browseNas, fetchNasAccount, nasFileKey, nasMailDocRef, type NasEntry } from '../lib/nas-api';
import type { MailDocRef } from '../lib/mail-api';
import { useDialogLayer } from '../lib/dialog-stack';
import { LoadingLines } from './ui/Skeleton';

/** 서버 MAX_TOTAL_ATTACH_BYTES 와 같은 값 */
const MAIL_MAX_BYTES = 25 * 1024 * 1024;

function fmtSize(n: number): string {
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}MB`;
  if (n >= 1024) return `${Math.round(n / 1024)}KB`;
  return `${n}B`;
}

const CHIP = 'px-2 py-0.5 text-xs rounded bg-slate-100 hover:bg-slate-200 text-slate-600';

export function NasFilePicker({
  open,
  existingKeys,
  onClose,
  onAdd,
}: {
  open: boolean;
  /** 이미 첨부 목록에 있는 NAS 파일 (nasFileKey) — 다시 못 고르게 */
  existingKeys: Set<string>;
  onClose: () => void;
  onAdd: (refs: MailDocRef[]) => void;
}) {
  const [path, setPath] = useState<string[]>([]);
  const [keyword, setKeyword] = useState('');
  const [picked, setPicked] = useState<Map<string, { path: string[]; entry: NasEntry }>>(new Map());
  // 입력이 있는 창이라 ESC 로 닫지 않는다 — 아래 메일 창으로 ESC 가 새지 않게 층만 쌓는다(lib/dialog-stack.ts)
  useDialogLayer(open, null);

  useEffect(() => {
    if (!open) return;
    setPicked(new Map());
    setKeyword('');
  }, [open]);

  const account = useQuery({ queryKey: ['nas', 'account'], queryFn: fetchNasAccount, enabled: open });
  const acc = account.data;
  const usable = !!acc?.connected && !!acc.ready;
  const listing = useQuery({
    queryKey: ['nas', 'browse', path],
    queryFn: () => browseNas(path),
    enabled: open && usable,
  });

  const entries = listing.data?.entries ?? [];
  const kw = keyword.trim().toLowerCase();
  const rows = useMemo(
    () => (kw ? entries.filter((e) => e.name.toLowerCase().includes(kw)) : entries),
    [entries, kw],
  );
  const pickedBytes = [...picked.values()].reduce((a, p) => a + p.entry.size, 0);

  if (!open) return null;

  const go = (next: string[]): void => {
    setPath(next);
    setKeyword('');
  };
  const toggle = (e: NasEntry): void => {
    const k = nasFileKey(path, e.name);
    const next = new Map(picked);
    if (next.has(k)) next.delete(k);
    else next.set(k, { path: [...path], entry: e });
    setPicked(next);
  };

  let body: ReactNode;
  if (account.isLoading) {
    body = <div className="text-center text-slate-400 py-8 text-sm">확인 중…</div>;
  } else if (account.isError) {
    body = <div className="px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-sm">{(account.error as Error).message}</div>;
  } else if (acc && !acc.ready) {
    body = <div className="text-center text-slate-500 py-8 text-sm">🚧 지금은 NAS 를 열 수 없습니다 — {acc.readyReason ?? '서버가 아직 준비되지 않았습니다.'}</div>;
  } else if (!acc?.connected) {
    body = (
      <div className="text-center text-slate-600 py-8 text-sm leading-relaxed">
        🔒 NAS 계정이 연결돼 있지 않습니다.
        <br />
        <a href="/mypage" target="_blank" rel="noopener" className="text-blue-600 underline">마이페이지 › NAS 계정 연결</a>
        {' '}에서 본인 NAS 아이디·비밀번호를 먼저 연결해 주세요.
      </div>
    );
  } else {
    body = (
      <>
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          <button type="button" onClick={() => go([])} className={CHIP}>공유 루트</button>
          {path.map((seg, i) => (
            <Fragment key={`${i}-${seg}`}>
              <span className="text-slate-300">›</span>
              {i === path.length - 1 ? (
                <span className="px-2 py-0.5 text-xs rounded bg-slate-700 text-white font-medium">{seg}</span>
              ) : (
                <button type="button" onClick={() => go(path.slice(0, i + 1))} className={CHIP}>{seg}</button>
              )}
            </Fragment>
          ))}
          <button type="button" onClick={() => go(path.slice(0, -1))} disabled={path.length === 0} className="btn btn-secondary btn-sm ml-1">↑ 위로</button>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="이 폴더에서 이름 찾기"
            className="ui-input w-44 ml-auto"
          />
        </div>
        {listing.isLoading ? (
          <div className="text-center text-slate-400 py-8 text-sm"><LoadingLines /></div>
        ) : listing.isError ? (
          <div className="px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-sm">
            {(listing.error as Error).message}
            <button type="button" onClick={() => void listing.refetch()} className="ml-2 underline">다시 시도</button>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center text-slate-400 py-8 text-sm">
            {entries.length === 0 ? '이 폴더에는 항목이 없습니다.' : `「${keyword.trim()}」 와(과) 맞는 이름이 없습니다.`}
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <tbody>
              {rows.map((e) => {
                if (e.isDir) {
                  return (
                    <tr key={`d:${e.name}`} className="border-t border-slate-100">
                      <td className="py-1.5 pr-2 w-6" />
                      <td className="py-1.5 pr-2" colSpan={2}>
                        <span className="mr-1.5">📁</span>
                        <button
                          type="button"
                          onClick={() => { if (e.name !== '.' && e.name !== '..') go([...path, e.name]); }}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {e.name}
                        </button>
                      </td>
                    </tr>
                  );
                }
                const k = nasFileKey(path, e.name);
                const already = existingKeys.has(k);
                const tooBig = e.size > MAIL_MAX_BYTES;
                return (
                  <tr key={`f:${e.name}`} className="border-t border-slate-100">
                    <td className="py-1.5 pr-2 w-6">
                      <input
                        type="checkbox"
                        disabled={already || tooBig}
                        checked={already || picked.has(k)}
                        onChange={() => toggle(e)}
                        title={already ? '이미 첨부했습니다' : tooBig ? '메일 한도 25MB 를 넘는 파일입니다' : undefined}
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <span className="mr-1.5">📄</span>
                      <span className={already || tooBig ? 'text-slate-400' : 'text-slate-800'}>{e.name}</span>
                      {tooBig && <span className="ml-1.5 text-[11px] text-rose-600">25MB 넘음</span>}
                    </td>
                    <td className="py-1.5 text-right text-xs text-slate-500 whitespace-nowrap">{fmtSize(e.size)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </>
    );
  }

  return (
    // 메일 쓰기 창(z-90) 위 — 포털 보관 문서 고르기 창과 같은 층
    <div className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="ui-subsection-title">🗃 NAS 파일 첨부</h3>
          <button type="button" onClick={onClose} className="ui-close">×</button>
        </div>
        <div className="px-5 pt-2 text-xs text-slate-500">
          본인 NAS 계정으로 엽니다 — 체크한 파일은 <b>보낼 때 서버가 NAS 에서 가져와</b> 붙입니다. 다른 폴더로 옮겨 가며 모아 고를 수 있습니다.
        </div>
        <div className="p-4 flex-1 overflow-y-auto">{body}</div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <span className="mr-auto text-xs text-slate-500">
            {picked.size ? `${picked.size}개 고름 · ${fmtSize(pickedBytes)}` : ''}
          </span>
          <button type="button" onClick={onClose} className="btn btn-secondary">취소</button>
          <button
            type="button"
            disabled={picked.size === 0}
            onClick={() => {
              onAdd([...picked.values()].map((p) => nasMailDocRef(p.path, p.entry)));
              onClose();
            }}
            className="btn btn-primary"
          >
            {picked.size ? `${picked.size}개 첨부` : '첨부'}
          </button>
        </div>
      </div>
    </div>
  );
}
